import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  OutlinedInput,
  Alert,
  CircularProgress,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import { Edit, Delete, Add, Close } from '@mui/icons-material';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

interface CreditPackage {
  externalId: string;
  name: string;
  description: string;
  price: number;
  discountPrice: number;
  credits: number;
  createdAt: string;
  updatedAt: string;
}

interface GroupPriceItem {
  groupId: string;
  groupName: string;
  price: number;
  discountPrice: number;
}

const CreditPackagesPage: React.FC = () => {
  const { t } = useTranslation();
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [editingPackage, setEditingPackage] = useState<CreditPackage | null>(
    null,
  );
  const [deletingPackage, setDeletingPackage] = useState<CreditPackage | null>(
    null,
  );
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [newPackage, setNewPackage] = useState<
    Omit<CreditPackage, 'externalId' | 'createdAt' | 'updatedAt'>
  >({
    name: '',
    description: '',
    price: 0,
    discountPrice: 0,
    credits: 0,
  });
  const [groupPrices, setGroupPrices] = useState<GroupPriceItem[]>([]);
  const [groupPricesTabIndex, setGroupPricesTabIndex] = useState<number>(0);

  // Load packages from API
  useEffect(() => {
    const loadPackages = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getCreditPackages();
        setPackages(response.packages);
      } catch (err: any) {
        setError(err.message || t('creditPackages.errors.failedToLoad'));
        console.error('Error loading credit packages:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPackages();
  }, []);

  const handleEdit = async (externalId: string) => {
    const packageToEdit = packages.find(p => p.externalId === externalId);
    if (packageToEdit) {
      setEditingPackage(packageToEdit);
      setIsEditModalOpen(true);
      try {
        const prices =
          await apiService.getCreditPackageGroupPrices(externalId);
        setGroupPrices(prices);
      } catch {
        setGroupPrices([]);
      }
    }
  };

  const handleDelete = (externalId: string) => {
    const packageToDelete = packages.find(p => p.externalId === externalId);
    if (packageToDelete) {
      setDeletingPackage(packageToDelete);
      setIsDeleteModalOpen(true);
    }
  };

  const handleAddPackage = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setNewPackage({
      name: '',
      description: '',
      price: 0,
      discountPrice: 0,
      credits: 0,
    });
  };

  const handleSavePackage = async () => {
    // Validate required fields
    if (
      !newPackage.name ||
      !newPackage.description ||
      newPackage.price <= 0 ||
      newPackage.credits <= 0
    ) {
      setSnackbar({
        open: true,
        message: t('creditPackages.messages.fillRequiredFields'),
        severity: 'error',
      });
      return;
    }

    try {
      const createdPackage = await apiService.createCreditPackage(newPackage);

      // Add the new package to the list
      setPackages(prev => [...prev, createdPackage]);

      // Show success message
      setSnackbar({
        open: true,
        message: t('creditPackages.messages.createdSuccess'),
        severity: 'success',
      });

      handleCloseModal();
    } catch (err: any) {
      console.error('Error creating credit package:', err);

      // Show error message
      setSnackbar({
        open: true,
        message: err.message || t('creditPackages.errors.failedToCreate'),
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingPackage(null);
    setGroupPrices([]);
    setGroupPricesTabIndex(0);
  };

  const handleGroupPriceChange = (
    groupId: string,
    field: 'price' | 'discountPrice',
    value: number,
  ) => {
    setGroupPrices(prev =>
      prev.map(item =>
        item.groupId === groupId ? { ...item, [field]: value } : item,
      ),
    );
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingPackage(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingPackage) return;

    try {
      await apiService.deleteCreditPackage(deletingPackage.externalId);

      // Remove the package from the list
      setPackages(prev =>
        prev.filter(p => p.externalId !== deletingPackage.externalId),
      );

      // Show success message
      setSnackbar({
        open: true,
        message: t('creditPackages.messages.deletedSuccess'),
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Error deleting credit package:', err);

      // Show error message
      setSnackbar({
        open: true,
        message: err.message || t('creditPackages.errors.failedToDelete'),
        severity: 'error',
      });
    } finally {
      // Always close modal
      handleCloseDeleteModal();
    }
  };

  const handleUpdatePackage = async () => {
    if (!editingPackage) return;

    // Validate required fields
    if (
      !editingPackage.name ||
      !editingPackage.description ||
      editingPackage.price <= 0 ||
      editingPackage.credits <= 0
    ) {
      setSnackbar({
        open: true,
        message: t('creditPackages.messages.fillRequiredFields'),
        severity: 'error',
      });
      return;
    }

    try {
      const updatedPackage = await apiService.updateCreditPackage(
        editingPackage.externalId,
        {
          name: editingPackage.name,
          description: editingPackage.description,
          price: editingPackage.price,
          discountPrice: editingPackage.discountPrice,
          credits: editingPackage.credits,
        },
      );

      // Update group prices
      for (const item of groupPrices) {
        await apiService.updateCreditPackageGroupPrice(
          editingPackage.externalId,
          item.groupId,
          { price: item.price, discountPrice: item.discountPrice },
        );
      }

      // Update the package in the list
      setPackages(prev =>
        prev.map(p =>
          p.externalId === editingPackage.externalId ? updatedPackage : p,
        ),
      );

      // Show success message
      setSnackbar({
        open: true,
        message: t('creditPackages.messages.updatedSuccess'),
        severity: 'success',
      });

      handleCloseEditModal();
    } catch (err: any) {
      console.error('Error updating credit package:', err);

      // Show error message
      setSnackbar({
        open: true,
        message: err.message || t('creditPackages.errors.failedToUpdate'),
        severity: 'error',
      });
    }
  };

  const handleEditInputChange =
    (field: keyof CreditPackage) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (editingPackage) {
        const value = event.target.value;
        setEditingPackage(prev =>
          prev
            ? {
                ...prev,
                [field]:
                  field === 'price' ||
                  field === 'discountPrice' ||
                  field === 'credits'
                    ? parseFloat(value) || 0
                    : value,
              }
            : null
        );
      }
    };

  const handleInputChange =
    (field: keyof typeof newPackage) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setNewPackage(prev => ({
        ...prev,
        [field]:
          field === 'price' || field === 'discountPrice' || field === 'credits'
            ? parseFloat(value) || 0
            : value,
      }));
    };

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('creditPackages.title')}
        </Typography>
        <Alert variant="filled" severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('creditPackages.title')}
      </Typography>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          {/* Search and Add Package Controls */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center',
              mb: 3,
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddPackage}
              sx={{ minWidth: '140px' }}
            >
              {t('creditPackages.addPackage')}
            </Button>
          </Box>

          {/* Table */}
          <Box sx={{ width: '100%', overflowX: 'auto', minWidth: 0 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table>
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="h6">{t('creditPackages.table.name')}</Typography></TableCell>
                  <TableCell><Typography variant="h6">{t('creditPackages.table.description')}</Typography></TableCell>
                  <TableCell><Typography variant="h6">{t('creditPackages.table.price')}</Typography></TableCell>
                  <TableCell><Typography variant="h6">{t('creditPackages.table.discountPrice')}</Typography></TableCell>
                  <TableCell><Typography variant="h6">{t('creditPackages.table.credits')}</Typography></TableCell>
                  <TableCell><Typography variant="h6">{t('creditPackages.table.createdAt')}</Typography></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {packages.map(pkg => (
                  <TableRow key={pkg.externalId}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {pkg.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {pkg.description}
                      </Typography>
                    </TableCell>
                    <TableCell>${pkg.price}</TableCell>
                    <TableCell>${pkg.discountPrice}</TableCell>
                    <TableCell>
                      <Chip label={pkg.credits} color="primary" size="small" />
                    </TableCell>
                    <TableCell>
                      {new Date(pkg.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(pkg.externalId)}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(pkg.externalId)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Add Package Modal */}
      <Dialog
        open={isAddModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{t('creditPackages.modals.add.title')}</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('creditPackages.modals.add.packageName')}</InputLabel>
              <OutlinedInput
                value={newPackage.name}
                onChange={handleInputChange('name')}
                label={t('creditPackages.modals.add.packageName')}
              />
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('creditPackages.modals.add.description')}</InputLabel>
              <OutlinedInput
                multiline
                rows={3}
                value={newPackage.description}
                onChange={handleInputChange('description')}
                label={t('creditPackages.modals.add.description')}
              />
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('creditPackages.modals.add.price')}</InputLabel>
                <OutlinedInput
                  type="number"
                  value={newPackage.price}
                  onChange={handleInputChange('price')}
                  label={t('creditPackages.modals.add.price')}
                />
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('creditPackages.modals.add.discountPrice')}</InputLabel>
                <OutlinedInput
                  type="number"
                  value={newPackage.discountPrice}
                  onChange={handleInputChange('discountPrice')}
                  label={t('creditPackages.modals.add.discountPrice')}
                />
              </FormControl>
            </Box>

            <FormControl fullWidth>
              <InputLabel>{t('creditPackages.modals.add.credits')}</InputLabel>
              <OutlinedInput
                type="number"
                value={newPackage.credits}
                onChange={handleInputChange('credits')}
                label={t('creditPackages.modals.add.credits')}
              />
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSavePackage}
            variant="contained"
            color="primary"
          >
            {t('creditPackages.modals.add.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Package Modal */}
      {editingPackage && (
        <Dialog
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">{t('creditPackages.modals.edit.title')}</Typography>
              <IconButton onClick={handleCloseEditModal} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}
            >
              <FormControl fullWidth>
                <InputLabel>{t('creditPackages.modals.edit.packageName')}</InputLabel>
                <OutlinedInput
                  value={editingPackage.name}
                  onChange={handleEditInputChange('name')}
                  label={t('creditPackages.modals.edit.packageName')}
                />
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('creditPackages.modals.edit.description')}</InputLabel>
                <OutlinedInput
                  multiline
                  rows={3}
                  value={editingPackage.description}
                  onChange={handleEditInputChange('description')}
                  label={t('creditPackages.modals.edit.description')}
                />
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('creditPackages.modals.edit.price')}</InputLabel>
                  <OutlinedInput
                    type="number"
                    value={editingPackage.price}
                    onChange={handleEditInputChange('price')}
                    label={t('creditPackages.modals.edit.price')}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('creditPackages.modals.edit.discountPrice')}</InputLabel>
                  <OutlinedInput
                    type="number"
                    value={editingPackage.discountPrice}
                    onChange={handleEditInputChange('discountPrice')}
                    label={t('creditPackages.modals.edit.discountPrice')}
                  />
                </FormControl>
              </Box>

              <FormControl fullWidth>
                <InputLabel>{t('creditPackages.modals.edit.credits')}</InputLabel>
                <OutlinedInput
                  type="number"
                  value={editingPackage.credits}
                  onChange={handleEditInputChange('credits')}
                  label={t('creditPackages.modals.edit.credits')}
                />
              </FormControl>

              {/* Group prices section */}
              <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3, mt: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  {t('creditPackages.modals.edit.groupPrices')}
                </Typography>
                {groupPrices.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('creditPackages.modals.edit.noGroupPrices')}
                  </Typography>
                ) : (
                  <>
                    <Tabs
                      value={groupPricesTabIndex}
                      onChange={(_, v: number) => setGroupPricesTabIndex(v)}
                      sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                    >
                      {groupPrices.map(item => (
                        <Tab key={item.groupId} label={item.groupName} />
                      ))}
                    </Tabs>
                    {groupPrices.map((item, index) => (
                      <div
                        key={item.groupId}
                        role="tabpanel"
                        hidden={groupPricesTabIndex !== index}
                        id={`group-price-tabpanel-${index}`}
                        aria-labelledby={`group-price-tab-${index}`}
                      >
                        {groupPricesTabIndex === index && (
                          <Box sx={{ display: 'flex', gap: 2, pt: 1 }}>
                            <FormControl fullWidth>
                              <InputLabel>
                                {t('creditPackages.modals.edit.price')}
                              </InputLabel>
                              <OutlinedInput
                                type="number"
                                value={item.price}
                                onChange={e =>
                                  handleGroupPriceChange(
                                    item.groupId,
                                    'price',
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                label={t('creditPackages.modals.edit.price')}
                              />
                            </FormControl>
                            <FormControl fullWidth>
                              <InputLabel>
                                {t('creditPackages.modals.edit.discountPrice')}
                              </InputLabel>
                              <OutlinedInput
                                type="number"
                                value={item.discountPrice}
                                onChange={e =>
                                  handleGroupPriceChange(
                                    item.groupId,
                                    'discountPrice',
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                label={t(
                                  'creditPackages.modals.edit.discountPrice',
                                )}
                              />
                            </FormControl>
                          </Box>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseEditModal} color="secondary">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleUpdatePackage}
              variant="contained"
              color="primary"
            >
              {t('creditPackages.modals.edit.update')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Modal */}
      {deletingPackage && (
        <Dialog
          open={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">{t('creditPackages.modals.delete.title')}</Typography>
              <IconButton onClick={handleCloseDeleteModal} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Typography>
              {t('creditPackages.modals.delete.confirm')}{' '}
              <strong>{deletingPackage.name}</strong>? {t('creditPackages.modals.delete.warning')}
            </Typography>
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDeleteModal} color="secondary">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
            >
              {t('creditPackages.modals.delete.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          variant="filled"
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreditPackagesPage;
